import AddOnService from '@spike/addon-service-model';
import { status as AppointmentStatuses } from '@spike/appointment-model';
import { NewAppointment } from '@spike/new-booking-model';
import { NewBookingsStatus, saveCreateAppointmentThunk } from '@spike/new-bookings-action';
import { Order, OrderizableTypes } from '@spike/order-model';
import { OrdersStatus, reloadOrderThunk, saveOrderItemThunk } from '@spike/orders-action';
import { Product } from '@spike/product-model';
import useNonInitialEffect from '@versiondos/hooks';
import ComplementaryItem from 'components/CreateBooking/ExtraItems';
import PetAndServices from 'components/CreateBooking/PetAndServices';
import { useApiClientWrapper, useAuth, useMarketplace } from 'hooks';
import {
    AddOnDate,
    itemTypes,
    Discount as MultipleBookingDiscount,
    PetServiceWrapper,
    versionPetAndServices
} from 'model/NewMultipleBooking';
import { Moment } from 'moment-timezone';
import { FunctionComponent, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
    convertDiscount,
    getOrderItemDescription,
    getOrderItemDiscount,
    getOrderItemOrderizable,
    getUsedPetIds,
    getUsedPetServices
} from './EditOrderItem';

export interface AddOrderItemProps {
    order: Order;
    bookingId: number;
    petId: number;
    type: OrderizableTypes;
    className?: string;
    onClose: () => void;
}

const getType = (orderizableType: OrderizableTypes): itemTypes | undefined => {
    switch (orderizableType) {
        case OrderizableTypes.APPOINTMENT:
            return itemTypes.service;
            break;
        case OrderizableTypes.ADDON_SERVICE:
            return itemTypes.addOn;
            break;
        case OrderizableTypes.PRODUCT:
            return itemTypes.product;
            break;
        case OrderizableTypes.CUSTOM:
            return itemTypes.custom;
            break;
        default:
            return undefined;
    }
};

const getUsedIds = (orderizableType: OrderizableTypes, order: Order, petId: number): Array<number> | undefined => {
    if (orderizableType === OrderizableTypes.ADDON_SERVICE) {
        return order.items.addOnServices
            .filter(item => item.orderItem.petId === petId)
            .map(item => item.addOnService.id!);
    } else if (orderizableType === OrderizableTypes.PRODUCT) {
        return order.items.products.filter(item => item.orderItem.petId === petId).map(item => item.product.id!);
    } else if (orderizableType === OrderizableTypes.CUSTOM) {
        return undefined;
    } else {
        return [];
    }
};

const getComplementaryItemProps = (
    order: Order,
    petId: number,
    orderizableType: OrderizableTypes
): {
    type?: itemTypes;
    parentId: number;
    petId?: number;
    date?: Moment;
    versionPetAndServices?: versionPetAndServices;
    itemsUsed?: Array<number>;
    dates: Array<AddOnDate>;
} => {
    const itemType = getType(orderizableType);
    const usedIds = getUsedIds(orderizableType, order, petId);

    return {
        parentId: order.client.id!,
        petId: petId,
        type: itemType,
        date: getNewServiceBookDate(order, petId),
        itemsUsed: usedIds,
        dates: order.items.addOnServices
            .filter(item => item.orderItem.petId === petId)
            .map(item => ({
                id: item.orderItem.orderizable.id!,
                date: item.orderItem.startedAt?.format('L') ?? ''
            }))
    };
};

const getNewServiceBookDate = (order: Order, petId: number) => {
    const bookDate = order.items.appointments
        .filter(item => item.appointment.pet.id === petId)
        .map(item => item.appointment.duration.to)
        .reduce<Moment | null>((lastTo, to) => (lastTo === null || lastTo?.isBefore(to) ? to.clone() : lastTo), null);

    return bookDate === null ? order.issuedAt : bookDate;
};

const getPetParentId = (order: Order, petId: number) => {
    const pet = order.items.appointments.find(item => item.pet.id === petId)?.pet;

    return pet ? pet.clientId : order.client.id!;
};

export const AddOrderItem: FunctionComponent<AddOrderItemProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const marketplace = useMarketplace();
    const auth = useAuth();

    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);
    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);

    const [saving, setSaving] = useState(false);

    useNonInitialEffect(() => {
        switch (ordersStatus) {
            case OrdersStatus.SaveOrderItemSuccess:
            case OrdersStatus.ReloadSuccess:
                setSaving(false);
                props.onClose();
                break;
            case OrdersStatus.Error:
                setSaving(false);
        }
    }, [ordersStatus]);

    useNonInitialEffect(() => {
        switch (newBookingsStatus) {
            case NewBookingsStatus.SavingCreateAppointmentSuccess:
                dispatch(reloadOrderThunk(apiClientWrapper));
                break;
            case NewBookingsStatus.Error:
                setSaving(false);
        }
    }, [newBookingsStatus]);

    const saveAppointmentHandler = (
        date: Moment | undefined,
        petId: number | undefined,
        service: PetServiceWrapper | undefined,
        staffId: number | undefined,
        discount: MultipleBookingDiscount | undefined
    ) => {
        setSaving(true);

        if (date && petId && service && auth.user) {
            const newAppointment: NewAppointment = {
                booking_id: props.bookingId,
                address_id: marketplace.basics.address.id as number,
                booked_at: date.clone(),
                booking_end_at: date.clone().add(service?.durationInMinutes, 'minutes'),
                customer_id: getPetParentId(props.order, petId),
                pet_id: petId,
                service_id: service?.service.id,
                staff_id: staffId,
                status: AppointmentStatuses.BOOKED,
                uuid: apiClientWrapper.uuid(),
                created_by: auth.user.staffId,
                discount_amount: discount?.type === 'amount' ? discount?.value.toFixed(2) : undefined,
                discount_percentage: discount?.type === 'percentage' ? discount?.value : undefined,
                order_item_price: service.price
            };

            dispatch(saveCreateAppointmentThunk(apiClientWrapper, newAppointment));
        }
    };

    const saveOrderItemHandler = (
        date?: Moment,
        petId?: number,
        selectedProduct?: Product,
        selectedAddOn?: AddOnService,
        custom?: string,
        staffId?: number,
        discount?: MultipleBookingDiscount,
        quantity?: number,
        amount?: number
    ) => {
        setSaving(true);

        const newOrderItem = {
            uuid: apiClientWrapper.uuid(),
            orderId: props.order.id,
            orderizable: getOrderItemOrderizable(selectedAddOn, selectedProduct, custom) ?? {
                type: OrderizableTypes.CUSTOM
            },
            petId: petId,
            staffId: staffId,
            description: getOrderItemDescription(selectedAddOn, selectedProduct, custom) ?? '',
            quantity: quantity ?? 1,
            amount: (amount ?? 0.0).toString(),
            discount: getOrderItemDiscount(discount),
            startedAt: date?.clone()
        };

        dispatch(saveOrderItemThunk(apiClientWrapper, newOrderItem));
    };

    const usedPetServicesIds: Array<number> = useMemo(
        () => getUsedPetServices(props.order, props.petId),
        [props.order, props.petId]
    );

    const usedPetIds: Array<number> = useMemo(() => getUsedPetIds(props.order), [props.order]);

    const complementaryItemProps = useMemo(
        () => getComplementaryItemProps(props.order, props.petId, props.type),
        [props.order, props.type, props.type]
    );

    return props.type == OrderizableTypes.APPOINTMENT ? (
        <PetAndServices
            parentId={props.order.client.id!}
            petId={props.petId}
            date={getNewServiceBookDate(props.order, props.petId)}
            versionPetAndServices={versionPetAndServices.add}
            petsUsed={usedPetIds}
            servicesUsed={usedPetServicesIds}
            discount={convertDiscount()}
            petWithoutSelect={true}
            loading={saving}
            onBack={props.onClose}
            onBook={saveAppointmentHandler}
        />
    ) : (
        <ComplementaryItem
            {...complementaryItemProps}
            versionPetAndServices={versionPetAndServices.add}
            petWithoutSelect={true}
            loading={saving}
            onBack={props.onClose}
            onBook={saveOrderItemHandler}
        />
    );
};

export default AddOrderItem;
